import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CategoryManager } from "@/components/shared/CategoryManager";
import { getTrainingMaterials, createTrainingMaterial, updateTrainingMaterial, deleteTrainingMaterial, getTrainingCategories, createTrainingCategory, updateTrainingCategory, deleteTrainingCategory, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, Plus, Pencil, Trash2, Loader2, Search, Settings, Video, FileText, ExternalLink, ImagePlus, X, ChevronLeft, ChevronRight, HelpCircle, Wrench } from "lucide-react";

const typeOptions = [
  { value: "video", label: "Video" },
  { value: "document", label: "Document" },
  { value: "link", label: "External Link" },
];
const typeIcons = { video: Video, document: FileText, link: ExternalLink };

const EMPTY_PAGE = { content: "", image_url: "", video_url: "" };
const EMPTY_QUESTION = { question: "", options: ["", "", "", ""], correct_answer: 0 };

function PageEditor({ pages, onChange }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [uploading, setUploading] = useState(false);

  const addPage = () => { onChange([...pages, { ...EMPTY_PAGE }]); setCurrentPage(pages.length); };
  const removePage = (idx) => {
    const next = pages.filter((_, i) => i !== idx);
    onChange(next);
    setCurrentPage(Math.max(0, Math.min(currentPage, next.length - 1)));
  };
  const updatePage = (field, value) => {
    const next = [...pages];
    next[currentPage] = { ...next[currentPage], [field]: value };
    onChange(next);
  };
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file, "training-pages");
      updatePage("image_url", result.url);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const page = pages[currentPage] || {};

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="page-editor">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-navy">Training Pages ({pages.length})</h4>
        <Button type="button" size="sm" variant="outline" onClick={addPage} data-testid="add-page-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Page
        </Button>
      </div>
      {pages.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No pages yet. Click "Add Page" to start building content.</p>
      ) : (
        <>
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={currentPage === 0} onClick={() => setCurrentPage(currentPage - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium text-navy">Page {currentPage + 1} of {pages.length}</span>
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={currentPage === pages.length - 1} onClick={() => setCurrentPage(currentPage + 1)}><ChevronRight className="w-4 h-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removePage(currentPage)} data-testid="remove-page-btn"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
          <div>
            <Label>Content</Label>
            <Textarea rows={4} value={page.content || ""} onChange={(e) => updatePage("content", e.target.value)} placeholder="Page content..." data-testid="page-content" />
          </div>
          <div>
            <Label>Image</Label>
            {page.image_url ? (
              <div className="flex items-center gap-2 mt-1">
                <img src={page.image_url} alt="" className="w-20 h-20 object-cover rounded border" />
                <Button type="button" variant="ghost" size="sm" onClick={() => updatePage("image_url", "")}><X className="w-3 h-3" /></Button>
              </div>
            ) : (
              <div className="mt-1">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id={`page-img-${currentPage}`} />
                <label htmlFor={`page-img-${currentPage}`}>
                  <div className="border border-dashed border-slate-200 rounded p-2 text-center cursor-pointer hover:border-teal/50 text-xs text-slate-400">
                    {uploading ? "Uploading..." : <><ImagePlus className="w-4 h-4 inline mr-1" />Upload image</>}
                  </div>
                </label>
              </div>
            )}
          </div>
          <div>
            <Label>Video URL (optional)</Label>
            <Input value={page.video_url || ""} onChange={(e) => updatePage("video_url", e.target.value)} placeholder="https://youtube.com/..." data-testid="page-video-url" />
          </div>
        </>
      )}
    </div>
  );
}

function QuizEditor({ quiz, onChange }) {
  const questions = quiz?.questions || [];

  const addQuestion = () => {
    onChange({ ...quiz, questions: [...questions, { ...EMPTY_QUESTION, options: ["", "", "", ""] }] });
  };
  const removeQuestion = (idx) => {
    onChange({ ...quiz, questions: questions.filter((_, i) => i !== idx) });
  };
  const updateQuestion = (idx, field, value) => {
    const next = [...questions];
    next[idx] = { ...next[idx], [field]: value };
    onChange({ ...quiz, questions: next });
  };
  const updateOption = (qIdx, oIdx, value) => {
    const next = [...questions];
    const opts = [...next[qIdx].options];
    opts[oIdx] = value;
    next[qIdx] = { ...next[qIdx], options: opts };
    onChange({ ...quiz, questions: next });
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="quiz-editor">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-navy">Quiz ({questions.length} questions)</h4>
        <Button type="button" size="sm" variant="outline" onClick={addQuestion} data-testid="add-question-btn">
          <HelpCircle className="w-3 h-3 mr-1" /> Add Question
        </Button>
      </div>
      <p className="text-xs text-slate-400">Dealers need 80% to pass. Mark the correct answer with the radio button.</p>
      {questions.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No quiz questions yet.</p>
      ) : (
        <div className="space-y-4 max-h-64 overflow-y-auto">
          {questions.map((q, qIdx) => (
            <div key={qIdx} className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`quiz-q-${qIdx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Question {qIdx + 1}</Label>
                  <Input value={q.question} onChange={(e) => updateQuestion(qIdx, "question", e.target.value)} placeholder="Enter question..." data-testid={`q-text-${qIdx}`} />
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400 mt-5" onClick={() => removeQuestion(qIdx)}><Trash2 className="w-3 h-3" /></Button>
              </div>
              <div className="space-y-1.5 pl-2">
                {q.options.map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`q-${qIdx}-correct`}
                      checked={q.correct_answer === oIdx}
                      onChange={() => updateQuestion(qIdx, "correct_answer", oIdx)}
                      className="accent-teal"
                      data-testid={`q-${qIdx}-radio-${oIdx}`}
                    />
                    <Input value={opt} onChange={(e) => updateOption(qIdx, oIdx, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oIdx)}`} className="h-8 text-xs" data-testid={`q-${qIdx}-opt-${oIdx}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminTraining() {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", type: "document", category: "", url: "", is_repair_content: false, pages: [], quiz: null });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogTab, setDialogTab] = useState("details");

  const fetchData = async () => {
    try {
      const [mats, cats] = await Promise.all([getTrainingMaterials(), getTrainingCategories()]);
      setMaterials(mats);
      setCategories(cats);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", description: "", type: "document", category: categories[0]?.value || "", url: "", is_repair_content: false, pages: [], quiz: null });
    setDialogTab("details");
    setDialogOpen(true);
  };
  const openEdit = (mat) => {
    setEditing(mat);
    setForm({
      title: mat.title, description: mat.description || "", type: mat.type || "document",
      category: mat.category || "", url: mat.url || "", is_repair_content: mat.is_repair_content || false,
      pages: mat.pages || [], quiz: mat.quiz || null,
    });
    setDialogTab("details");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.quiz || !payload.quiz.questions?.length) payload.quiz = null;
      if (editing) { await updateTrainingMaterial(editing.id, payload); toast.success("Material updated"); }
      else { await createTrainingMaterial(payload); toast.success("Material created"); }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error("Failed to save material"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    try { await deleteTrainingMaterial(id); toast.success("Material deleted"); fetchData(); }
    catch (err) { toast.error("Failed to delete"); }
  };

  const filtered = materials.filter((m) => search ? (m.title?.toLowerCase().includes(search.toLowerCase()) || m.description?.toLowerCase().includes(search.toLowerCase())) : true);
  const getCatLabel = (val) => categories.find((c) => c.value === val)?.label || val;

  return (
    <AdminLayout title="Training Center" breadcrumb="Training">
      <div className="space-y-6" data-testid="admin-training">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search materials..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="training-search" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCatManagerOpen(true)} data-testid="manage-training-categories-btn">
              <Settings className="w-4 h-4 mr-2" /> Categories
            </Button>
            <Button className="bg-teal hover:bg-teal/90" onClick={openCreate} data-testid="create-training-btn">
              <Plus className="w-4 h-4 mr-2" /> New Material
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">{search ? "No matching materials" : "No training materials yet"}</p></CardContent></Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((mat) => {
              const TypeIcon = typeIcons[mat.type] || FileText;
              const hasPages = mat.pages?.length > 0;
              const hasQuiz = mat.quiz?.questions?.length > 0;
              return (
                <Card key={mat.id} className="border-slate-200" data-testid={`training-${mat.id}`}>
                  <CardContent className="p-4 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0"><TypeIcon className="w-5 h-5 text-indigo-500" /></div>
                      <div className="min-w-0">
                        <h3 className="font-heading font-semibold text-navy text-sm">{mat.title}</h3>
                        {mat.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{mat.description}</p>}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{getCatLabel(mat.category)}</span>
                          <span className="text-xs text-slate-400 capitalize">{mat.type}</span>
                          {hasPages && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{mat.pages.length} pages</span>}
                          {hasQuiz && <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">{mat.quiz.questions.length} quiz Q's</span>}
                          {mat.is_repair_content && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1"><Wrench className="w-3 h-3" />Repair</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(mat)} data-testid={`edit-training-${mat.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(mat.id)} data-testid={`delete-training-${mat.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Material Dialog with Tabs */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="training-dialog">
            <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "Edit Material" : "New Material"}</DialogTitle></DialogHeader>
            <div className="flex gap-2 mb-4">
              {["details", "pages", "quiz"].map((t) => (
                <button key={t} onClick={() => setDialogTab(t)} className={`px-3 py-1.5 rounded text-xs font-medium capitalize transition-colors ${dialogTab === t ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`dialog-tab-${t}`}>{t}</button>
              ))}
            </div>

            {dialogTab === "details" && (
              <div className="space-y-4">
                <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="training-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="training-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{typeOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="training-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>URL (Video/Link)</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://youtube.com/..." data-testid="training-url" /></div>
                <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="training-desc" /></div>
                <div className="flex items-center gap-3 bg-amber-50 rounded-lg p-3">
                  <Switch checked={form.is_repair_content} onCheckedChange={(v) => setForm({ ...form, is_repair_content: v })} data-testid="repair-content-toggle" />
                  <div><Label className="text-sm text-amber-800 flex items-center gap-1"><Wrench className="w-4 h-4" />Device Repair Content</Label><p className="text-xs text-amber-600">Only visible to dealers in the repair program</p></div>
                </div>
              </div>
            )}

            {dialogTab === "pages" && (
              <PageEditor pages={form.pages} onChange={(p) => setForm({ ...form, pages: p })} />
            )}

            {dialogTab === "quiz" && (
              <QuizEditor quiz={form.quiz || { questions: [] }} onChange={(q) => setForm({ ...form, quiz: q })} />
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-training-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Manager */}
        <Dialog open={catManagerOpen} onOpenChange={setCatManagerOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-navy">Manage Training Categories</DialogTitle></DialogHeader>
            <CategoryManager
              categories={categories}
              onCreate={async (d) => { await createTrainingCategory(d); fetchData(); }}
              onUpdate={async (id, d) => { await updateTrainingCategory(id, d); fetchData(); }}
              onDelete={async (id) => { await deleteTrainingCategory(id); fetchData(); }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
